// Unified keyboard + gamepad input with per-device "controllers".

export const DEVICES = {
  kbA: {
    label: 'Keyboard (WASD)',
    up: ['KeyW'], down: ['KeyS'], left: ['KeyA'], right: ['KeyD'],
    grab: ['Space'], use: ['KeyE', 'KeyF'], dash: ['ShiftLeft'], swap: ['Tab', 'KeyQ'],
    hints: { move: 'WASD', grab: 'Space', use: 'E', dash: 'L-Shift', swap: 'Q / Tab' },
    keys: { move: ['wasd'], grab: ['Space'], use: ['E'], dash: ['LShift'], swap: ['Q', 'Tab'] },
  },
  kbB: {
    label: 'Keyboard (Arrows)',
    up: ['ArrowUp'], down: ['ArrowDown'], left: ['ArrowLeft'], right: ['ArrowRight'],
    grab: ['Enter', 'Period', 'NumpadEnter', 'Numpad0'], use: ['ShiftRight', 'Slash', 'NumpadDecimal'], dash: ['ControlRight', 'Comma', 'Numpad1'], swap: ['Backspace', 'Quote'],
    hints: { move: 'Arrows', grab: 'Enter / .', use: 'R-Shift / /', dash: 'R-Ctrl / ,', swap: 'Backspace' },
    keys: { move: ['arrows'], grab: ['Enter', '.'], use: ['RShift', '/'], dash: ['RCtrl', ','], swap: ['Backspace'] },
  },
};

// ---------------------------------------------------------------- keyboard & mouse glyphs
const kc = (inner, cls = '') => `<span class="kc${cls ? ' ' + cls : ''}">${inner}</span>`;
/** One keyboard key (or key cluster) drawn as a keycap. */
export function keyCap(k) {
  switch (k) {
    case 'wasd': return `<span class="kcl">${kc('W', 'sm u')}${kc('A', 'sm')}${kc('S', 'sm')}${kc('D', 'sm')}</span>`;
    case 'arrows': return `<span class="kcl">${kc('▲', 'sm u ar')}${kc('◀', 'sm ar')}${kc('▼', 'sm ar')}${kc('▶', 'sm ar')}</span>`;
    case 'Space': return kc('<i class="sp"></i><small>space</small>', 'space');
    case 'LShift': return kc('⇧<small>L shift</small>', 'wide');
    case 'RShift': return kc('⇧<small>R shift</small>', 'wide');
    case 'RCtrl': return kc('ctrl<small>R</small>', 'wide txt');
    case 'Enter': return kc('⏎<small>enter</small>', 'wide');
    case 'Tab': return kc('⇥<small>tab</small>', 'wide');
    case 'Backspace': return kc('⌫<small>back</small>', 'wide');
    case 'Esc': return kc('esc', 'txt');
    case 'F3': return kc('F3', 'txt');
    default: return kc(k);
  }
}
/** Mouse glyph (left button highlighted). */
export const MOUSE_GLYPH = `<span class="kms"><svg viewBox="0 0 14 20"><rect x="1" y="1" width="12" height="18" rx="6"/><path class="lb" d="M7 1.2 A5.8 5.8 0 0 0 1.2 7 V8.5 H7 Z"/><path d="M7 1 V8.5 M1 8.5 H13"/></svg></span>`;
/** Keyboard hints as keycap HTML (all alternatives, or just the primary key). */
export function kbHints(dev, primary = false) {
  const K = DEVICES[dev].keys, out = {};
  for (const a in K) out[a] = (primary ? K[a].slice(0, 1) : K[a]).map(keyCap).join('<span class="kor">/</span>');
  return out;
}

// ---------------------------------------------------------------- gamepad styles
// The browser reports face buttons by POSITION (0 bottom, 1 right, 2 left, 3 top).
// What is printed on them depends on the controller family, so prompts and the
// action layout follow the chosen style. Switch-style pads use Nintendo conventions
// (A = grab, B = dash), like Overcooked on Switch.
export const PAD_STYLES = {
  xbox: { label: 'Xbox (A bottom)', names: ['A', 'B', 'X', 'Y'], cls: 'xb', map: { grab: 0, dash: 1, use: 2, swap: 3 }, ok: 0, back: 1 },
  nintendo: { label: 'Switch (B bottom, A right)', names: ['B', 'A', 'Y', 'X'], cls: 'ns', map: { grab: 1, dash: 0, use: 2, swap: 3 }, ok: 1, back: 0 },
  playstation: { label: 'PlayStation (✕ bottom)', names: ['✕', '○', '□', '△'], cls: 'ps', map: { grab: 0, dash: 1, use: 2, swap: 3 }, ok: 0, back: 1 },
};
const POS = ['bottom', 'right', 'left', 'top'];
/** Guess the controller family from the id the browser reports. */
export function detectPadStyle(id = '') {
  const s = id.toLowerCase();
  if (/057e|nintendo|switch|pro controller|joy-con/.test(s)) return 'nintendo';
  if (/054c|dualsense|dualshock|playstation|ps4|ps5/.test(s)) return 'playstation';
  return 'xbox';
}
/** Inline glyph: a mini diamond showing which face button, plus its printed label. */
export function padGlyph(style, btn) {
  const st = PAD_STYLES[style] || PAD_STYLES.xbox;
  const dots = [[6, 10.2], [10.2, 6], [1.8, 6], [6, 1.8]]
    .map(([x, y], i) => `<circle cx="${x}" cy="${y}" r="1.7"${i === btn ? ' class="on"' : ''}/>`)
    .join('');
  return `<span class="pg ${st.cls} p${btn}" title="${POS[btn]} face button"><svg viewBox="0 0 12 12">${dots}</svg><i>${st.names[btn]}</i></span>`;
}
/** Hints (HTML) for a gamepad in a given style. */
export function padHints(style) {
  const st = PAD_STYLES[style] || PAD_STYLES.xbox;
  const g = (a) => padGlyph(style, st.map[a]);
  return { move: '<span class="pg-txt">Stick</span>', grab: g('grab'), use: g('use'), dash: g('dash'), swap: g('swap') };
}

class Input {
  constructor() {
    this.padStyleSetting = 'auto';
    this.lastDevice = 'kb';
    this.keys = new Set();
    this.pressed = new Set();
    this.padPrev = {};
    this.padNow = {};
    this.listeners = [];
    window.addEventListener('keydown', (e) => {
      if (e.code === 'Tab' || e.code.startsWith('Arrow') || e.code === 'Space') {
        if (!(e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT'))) e.preventDefault();
      }
      if (!e.repeat) this.pressed.add(e.code);
      this.lastDevice = 'kb';
      this.keys.add(e.code);
      for (const l of this.listeners) l(e.code, 'key');
    });
    window.addEventListener('keyup', (e) => this.keys.delete(e.code));
    // mouse travel, for aiming the Delegate wheel
    this.mouseX = 0;
    this.mouseY = 0;
    window.addEventListener('mousemove', (e) => {
      this.mouseX += e.movementX || 0;
      this.mouseY += e.movementY || 0;
    });
    window.addEventListener('blur', () => this.keys.clear());
  }
  onPress(fn) {
    this.listeners.push(fn);
    return () => (this.listeners = this.listeners.filter((f) => f !== fn));
  }
  /** Style for pad index i (setting, or auto-detected from its id). */
  padStyle(i) {
    if (this.padStyleSetting && this.padStyleSetting !== 'auto') return this.padStyleSetting;
    const p = this.pads().find((q) => q.index === +i);
    return detectPadStyle(p ? p.id : '');
  }
  /** Style of the first connected pad (for generic prompts). */
  get anyPadStyle() {
    const p = this.pads()[0];
    return p ? this.padStyle(p.index) : this.padStyleSetting !== 'auto' ? this.padStyleSetting : 'xbox';
  }
  /** Hints for a device id: keyboard hints or pad glyphs. */
  hintsFor(dev, primary = false) {
    if (dev === 'any') return soloHints(primary);
    if (dev.startsWith('kb')) return kbHints(dev, primary);
    return padHints(this.padStyle(+dev.slice(3)));
  }
  isStandard(i) {
    const p = this.pads().find((q) => q.index === +i);
    return !p || p.mapping === 'standard';
  }
  pads() {
    const out = [];
    const gp = navigator.getGamepads ? navigator.getGamepads() : [];
    for (const p of gp) if (p && p.connected) out.push(p);
    return out;
  }
  /** call once per frame before reading */
  poll() {
    this.padPrev = this.padNow;
    this.padNow = {};
    for (const p of this.pads()) {
      this.padNow[p.index] = { b: p.buttons.map((b) => b.pressed), a: p.axes.slice() };
    }
    // emit pad button presses to listeners (menu navigation)
    for (const idx in this.padNow) {
      const now = this.padNow[idx].b, prev = (this.padPrev[idx] || { b: [] }).b;
      now.forEach((v, i) => {
        if (v && !prev[i]) {
          this.lastDevice = 'pad' + idx;
          for (const l of this.listeners) l('Pad' + idx + ':' + i, 'pad');
        }
      });
      // stick as dpad for menus
      const a = this.padNow[idx].a, pa = (this.padPrev[idx] || { a: [0, 0] }).a;
      const dir = (v) => (v > 0.6 ? 1 : v < -0.6 ? -1 : 0);
      if (dir(a[0]) || dir(a[1])) this.lastDevice = 'pad' + idx;
      if (dir(a[1]) !== dir(pa[1] || 0) && dir(a[1])) for (const l of this.listeners) l('Pad' + idx + ':' + (dir(a[1]) < 0 ? 12 : 13), 'pad');
      if (dir(a[0]) !== dir(pa[0] || 0) && dir(a[0])) for (const l of this.listeners) l('Pad' + idx + ':' + (dir(a[0]) < 0 ? 14 : 15), 'pad');
    }
  }
  endFrame() {
    this.pressed.clear();
  }
  down(codes) {
    return codes.some((c) => this.keys.has(c));
  }
  hit(codes) {
    return codes.some((c) => this.pressed.has(c));
  }
  padBtn(idx, b) {
    const p = this.padNow[idx];
    return !!(p && p.b[b]);
  }
  padHit(idx, b) {
    const p = this.padNow[idx], q = this.padPrev[idx];
    return !!(p && p.b[b] && !(q && q.b[b]));
  }
  /** Read a controller state for a device id: 'kbA' | 'kbB' | 'pad0'..'pad3' */
  read(dev) {
    const s = { mx: 0, mz: 0, grab: false, use: false, useHeld: false, dash: false, swap: false, swapHeld: false };
    if (dev.startsWith('kb')) {
      const d = DEVICES[dev];
      s.mx = (this.down(d.right) ? 1 : 0) - (this.down(d.left) ? 1 : 0);
      s.mz = (this.down(d.down) ? 1 : 0) - (this.down(d.up) ? 1 : 0);
      s.grab = this.hit(d.grab);
      s.use = this.hit(d.use);
      s.useHeld = this.down(d.use);
      s.dash = this.hit(d.dash);
      s.swap = this.hit(d.swap);
      s.swapHeld = this.down(d.swap);
    } else if (dev.startsWith('pad')) {
      const i = +dev.slice(3);
      const p = this.padNow[i];
      if (p) {
        let x = p.a[0] || 0, z = p.a[1] || 0;
        const m = Math.hypot(x, z);
        if (m < 0.22) { x = 0; z = 0; } else { const k = Math.min(1, (m - 0.22) / 0.7) / m; x *= k; z *= k; }
        if (p.b[14]) x = -1; if (p.b[15]) x = 1; if (p.b[12]) z = -1; if (p.b[13]) z = 1;
        s.mx = x; s.mz = z;
        const mp = (PAD_STYLES[this.padStyle(i)] || PAD_STYLES.xbox).map;
        s.grab = this.padHit(i, mp.grab);
        s.use = this.padHit(i, mp.use);
        s.useHeld = this.padBtn(i, mp.use);
        s.dash = this.padHit(i, mp.dash) || this.padHit(i, 7) || this.padHit(i, 6);
        s.swap = this.padHit(i, mp.swap) || this.padHit(i, 4) || this.padHit(i, 5);
        s.swapHeld = this.padBtn(i, mp.swap) || this.padBtn(i, 4) || this.padBtn(i, 5);
      }
    }
    const l = Math.hypot(s.mx, s.mz);
    if (l > 1) { s.mx /= l; s.mz /= l; }
    return s;
  }
  /** Mouse travel since the last call. */
  takeMouse() {
    const m = [this.mouseX, this.mouseY];
    this.mouseX = this.mouseY = 0;
    return m;
  }
  /** Every local device id: both keyboard layouts and each connected pad. */
  devices() {
    return ['kbA', 'kbB', ...this.pads().map((p) => 'pad' + p.index)];
  }
  /** Solo play: any keyboard or controller drives the one player (except devices claimed by other players here). */
  readAny(claimed = null) {
    const devs = this.devices().filter((d) => !claimed || !claimed.includes(d));
    const out = { mx: 0, mz: 0, grab: false, use: false, useHeld: false, dash: false, swap: false, swapHeld: false };
    for (const d of devs) {
      const s = this.read(d);
      out.mx += s.mx; out.mz += s.mz;
      for (const k of ['grab', 'use', 'useHeld', 'dash', 'swap', 'swapHeld']) out[k] = out[k] || s[k];
    }
    const l = Math.hypot(out.mx, out.mz);
    if (l > 1) { out.mx /= l; out.mz /= l; }
    return out;
  }
  /** Menu confirm / back button index for a pad. */
  menuButtons(i) {
    const st = PAD_STYLES[this.padStyle(i)] || PAD_STYLES.xbox;
    return { ok: st.ok, back: st.back };
  }
}

export const input = new Input();

/** Hints for whichever device the solo player last touched. */
export function soloHints(primary = false) {
  const d = input.lastDevice;
  return d.startsWith('pad') ? input.hintsFor(d) : kbHints('kbA', primary);
}
